// Text builders for the Security Insights "GRC Framework Coverage" rows when Features:GrcCoverageV2
// is on: the bar shows the readiness of the controls that have connector evidence (the Axaris
// Readiness Indicator over pass / partial / fail), the second line shows how many of the
// framework's controls have that evidence, and full-catalog coverage moves to the tooltip.
// Pure functions so the zero-evidence case ("—", never "0%") is unit-testable without rendering.
//
// `est` is a ComplianceEstimateDto row (observedControls, canonicalControlCount, passingControls,
// partialControls, failingControls, observabilityPercent, confidencePercent,
// adjustedCoveragePercent) or null when the framework is outside the vertical's scoring scope.
// `coverageLine` is the caller's catalog-coverage arithmetic (e.g. "Coverage = 12 ÷ 61 = 20%").
// Guided-question answers are client-side only and are NOT in observedControls — they count
// toward catalog coverage, never toward the "connector evidence" line.

export const NO_EVIDENCE_SENTENCE = "No controls currently have connector evidence.";

const hasObservations = (est) => !!est && Number(est.observedControls) > 0;

// A framework with no published control count has nothing to divide by, so it never shows a
// percentage — here or anywhere else. An OfficialCount framework shows the plain percentage; the
// tooltip says it is an estimate.
const isUnscorable = (est) => est?.readinessBasis === "Unscorable";
const isEstimate = (est) => est?.readinessBasis === "OfficialCount";

export function confidenceLabel(pct) {
  return pct >= 67 ? "High" : pct >= 34 ? "Medium" : "Low";
}

// Bar number + fill. pct is null (rendered "—", neutral bar at 0) whenever there is no estimate
// or nothing has been observed; zero assessed controls must never render as 0% readiness.
export function buildReadinessPrimary(est) {
  if (!hasObservations(est)) return { label: "—", pct: null };
  if (isUnscorable(est)) return { label: "Not scored", pct: null };
  const pct = Math.round(est.adjustedCoveragePercent);
  return { label: `${pct}%`, pct };
}

export function buildReadinessSecondLine(est, coverageLine) {
  if (!est) return coverageLine;
  if (!hasObservations(est)) return NO_EVIDENCE_SENTENCE;
  if (isUnscorable(est)) {
    return `${est.observedControls} controls have connector evidence · this framework publishes no official control count, so no readiness percentage is calculated`;
  }
  return `${est.observedControls} of ${est.canonicalControlCount} controls have connector evidence · ${Math.round(est.observabilityPercent)}% observability · ${confidenceLabel(est.confidencePercent)} confidence`;
}

export function buildReadinessTooltip(m, est, coverageLine) {
  if (!est) {
    return `${m.full}\n${coverageLine} (satisfied via Implemented/Partial controls + guided answers).`;
  }
  if (!hasObservations(est)) {
    return `${m.full}\n${NO_EVIDENCE_SENTENCE}\nCatalog coverage: ${coverageLine}`;
  }
  // "of N assessed controls" was true only while the indicator divided by the assessed subset. For a
  // framework with a published catalog it now divides by the whole catalog, so naming the assessed
  // count as the denominator would misstate the figure; the counts are reported as counts instead.
  const counts = `${est.passingControls} pass · ${est.partialControls} partial · ${est.failingControls} fail of ${est.observedControls} controls with evidence`;
  const headline = isUnscorable(est)
    ? "Readiness not scored — this framework publishes no official control count"
    : isEstimate(est)
      ? `Readiness ${Math.round(est.adjustedCoveragePercent)}% (estimate — based on the framework's published control count)`
      : `Readiness ${Math.round(est.adjustedCoveragePercent)}%`;
  return `${m.full}\n${headline} — ${counts}\nCatalog coverage: ${coverageLine}. Metric definitions and estimator method: ⓘ in the card header.`;
}
